import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Autocomplete as MuiAutocomplete, Box, Collapse, IconButton, TextField, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

function isEmpty(obj) {
  return Object.keys(obj).length === 0;
}

const Autocomplete = (props) => {
  return (
    <MuiAutocomplete
      sx={{
        '& .hotglue-w-MuiAutocomplete-option': {
          padding: '8px',
          '&:hover': {
            backgroundColor: '#fafafa',
            cursor: 'pointer',
          },
        },
      }}
      {...props}
    />
  );
};

const RowMap = (props) => {
  const { setMap, map, table, type: schemaType, required, sourceFields, index, error } = props;
  const [schemaTableMapped, setTableMapped] = useState('');
  const [open, setOpen] = useState(false);
  
  const flowId = useSelector((state) => state.flows.selectedFlow);
  const schemas = useSelector((state) => state.schemas[flowId]);
  const localization = useSelector((state) => state.configuration.localization);
  const selectedEntity = useSelector((state) => state.entities.selectedEntity);
  const enforceOneToOneMapping = useSelector((state) => state.configuration.enforceOneToOneMapping);
  const [isMounted, setIsMounted] = useState(false);

  const disabledSourceTables = useMemo(() => {
    if (!enforceOneToOneMapping) return new Set();
    const sourceTablesUsedByOthers = new Set();
    Object.keys(map || {}).forEach((key) => {
      const [tableSchemaKey, tableFieldMapKey] = key.split('/');
      if (tableSchemaKey && tableSchemaKey !== table && tableFieldMapKey) {
        sourceTablesUsedByOthers.add(tableFieldMapKey);
      }
    });
    return sourceTablesUsedByOthers;
  }, [map, table, enforceOneToOneMapping]);

  const fieldsSelected = useMemo(() => {
    const lookup = `${table}/${schemaTableMapped}`;
    return map && map[lookup] ? Object.values(map[lookup])?.length : 0;
  }, [schemaTableMapped, map]);

  const handleMapTable = (sourceTable, schemaTable = undefined) => {
    const newMap = Object.assign({}, map);
    for (const key in newMap) {
      const tableMapped = key.split('/')[0];
      const innerFieldTableMapped = key.split('/')[1];
      if (tableMapped === table && innerFieldTableMapped !== sourceTable) delete newMap[key];
    }

    const lookupTable = `${schemaTable ?? table}/${sourceTable}`;
    // debugger
    setMap({ ...newMap, [lookupTable]: { ...newMap?.[lookupTable] } });
    setTableMapped(sourceTable);
  };

  const handleMapField = ({ schemaField, sourceField, sourceTable, schemaTable, type }) => {
    const lookup = `${schemaTable}/${sourceTable}`;
    const newMap = {
      ...map,
      [lookup]: {
        ...map[lookup],
        [schemaField]: sourceField,
      },
    };

    if (!sourceField) {
      delete newMap[lookup][schemaField];
    }

    if (error[schemaField]) delete error[schemaField];

    // check if there is an enum in the schema to map and update the table of it
    if (type === 'enum') updateEnum(newMap, schemaField, sourceField);

    setMap(newMap);
  };

  const updateEnum = (map, schemaField, sourceField) => {
    const enumTable = schemas.find((schema) => schema.type === 'enum' && schema.table.split('.')[1] === schemaField);
    // delete if exists
    Object.keys(map).forEach((key) => {
      if (key.split('/')[0] === enumTable.table) delete map[key];
    });

    // set it if it doesn't exist
    if (map[`${enumTable.table}/${sourceField}`] === undefined) map[`${enumTable.table}/${sourceField}`] = {};
    return map;
  };

  // set the enum table when the map enum schema change
  useEffect(() => {
    if (schemaType === 'enum' && isMounted) {
      Object.keys(map).forEach((key) => {
        const [tableSchemaKey, tableFieldMapKey] = key.split('/');
        if (tableSchemaKey === table) {
          setTableMapped(tableFieldMapKey);
        }
      });
    }
  }, [map, isMounted]);

  // load all tables
  useEffect(() => {
    if (map && Object.values(map).length > 0) {
      setOpen(true);
    }

    if (map && Object.keys(map).length > 0 && !schemaTableMapped && !isMounted) {
      for (const key in map) {
        if (schemaTableMapped) continue;

        const [tableSchemaKey, tableFieldMapKey] = key.split('/');
        if (tableSchemaKey === table) {
          handleMapTable(tableFieldMapKey);
        }
      }
    }
    setIsMounted(true);
  }, []);

  useEffect(() => {
    schemas
      .filter((schema) => schema.table === table)
      .shift()
      .fields.forEach((field) => {
        if (error?.[`${table}/${field.id}`]) setOpen(true);
      });
  }, [error]);
  
  const tableName = table?.replace(/_\d+$/, '');

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{
        border: ({ palette }) => `1px solid ${palette.common.outline}`,
        p: 2,
      }}
      borderRadius={2}
    >
      <Box display="flex" width="100%" alignItems="center">
        <Box display="flex" p={1.5} gap={3} flexDirection="column" width="100%" justifyContent={'space-between'}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" gap={2} alignItems="center">
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="32px"
                width="32px"
                height="32px"
                sx={{ border: ({ palette }) => `1px solid ${palette.common.outline}` }}
              >
                <Typography variant="body2" fontWeight="400">
                  {index + 1}
                </Typography>
              </Box>
              <Box sx={{ maxWidth: '320px' }}>
                <Typography variant="body1" fontWeight="400">
                  {localization?.mappingRowCard?.header
                    ? localization?.mappingRowCard?.header
                    : schemaType !== 'enum'
                    ? `Map ${tableName}`
                    : `Map enum values for ${table.split('.')[0]} field`}
                </Typography>
                <Typography variant="body2" color="text.light">
                  {schemaType === 'enum'
                    ? 'Fill in the enum values of the selected field'
                    : `Select a table from ${selectedEntity.label}, then map your fields`}
                </Typography>
              </Box>
            </Box>
            <Box display="flex" bgcolor={fieldsSelected === 0 ? 'warning.main' : 'success.main'} borderRadius="10px">
              <Typography variant="body2" fontSize="12px" color="common.white" sx={{ px: 1, py: '2px' }}>
                {fieldsSelected} FIELDS
              </Typography>
            </Box>
          </Box>
          <Box display="flex" gap={2} alignItems="end">
            <TextField fullWidth value={tableName} variant="standard" onChange={(e) => {}} />
            <Typography variant="body2" p={'5px 0px'}>
              To
            </Typography>
            {schemaType === 'enum' ? (
              <TextField
                fullWidth
                variant="standard"
                contentEditable={false}
                value={schemaTableMapped}
                label={'Enumerator Field'}
                error={!!error[table]}
                required={!!required}
              />
            ) : (
              <Autocomplete
                fullWidth
                disablePortal
                value={schemaTableMapped}
                options={schemaType !== 'enum' ? Object.keys(sourceFields) : []}
                getOptionDisabled={enforceOneToOneMapping ? (option) => disabledSourceTables.has(option) : undefined}
                ListboxProps={{
                  sx: {
                    li: {
                      padding: '8px',
                      '&:hover': {
                        backgroundColor: '#fafafa',
                        cursor: 'pointer',
                      },
                      ...(enforceOneToOneMapping && {
                        '&[aria-disabled="true"]': {
                          opacity: 0.5,
                          pointerEvents: 'none',
                        },
                      }),
                    },
                  },
                }}
                size="small"
                sx={{
                  '.hotglue-w-MuiFormLabel-asterisk': {
                    color: ({ palette }) => palette.error.main,
                  },
                  '& button': {
                    border: 'none',
                  },
                  '&:hover button': {
                    visibility: 'visible',
                  },
                  '.hotglue-w-MuiAutocomplete-endAdornment': {
                    top: 'calc(40%)',
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    variant="standard"
                    contentEditable={true}
                    label={schemaType === 'enum' ? 'Enumerator Field' : 'table'}
                    {...params}
                    error={!!error[table]}
                    required={!!required}
                  />
                )}
                onChange={(e, newValue) => handleMapTable(newValue)}
              />
            )}
            <IconButton onClick={() => setOpen(!open)}>{open ? <ExpandLess /> : <ExpandMore />}</IconButton>
          </Box>
        </Box>
      </Box>
      <Collapse in={open}>
        <Box bgcolor="#FAFAFA" padding="8px" width="100%" display="flex" flexDirection="column">
          {schemas
            .filter((schema) => schema.table === table)
            .shift()
            .fields.map((field) => {
              const { id: fieldId, type } = field;
              const lookup = `${table}/${schemaTableMapped}`;
              return (
                <Box display="flex" key={field.id} width="100%">
                  <Box display="flex" gap={2} alignItems="end" flex="1" padding="8px">
                    <TextField fullWidth value={field.name} variant="standard" onChange={(e) => {}} />
                    <Typography variant="body2" p={'5px 0px'}>
                      =
                    </Typography>
                    {schemaType === 'enum' ? (
                      <TextField
                        size="small"
                        fullWidth
                        variant="standard"
                        label="Field"
                        value={map?.[lookup]?.[fieldId] ?? ''}
                        required={field.required ?? false}
                        error={!!error[`${table}/${fieldId}`]}
                        onChange={(e) => {
                          handleMapField({
                            schemaField: field.id,
                            sourceField: e.target.value,
                            sourceTable: schemaTableMapped,
                            schemaTable: table,
                            type,
                          });
                        }}
                      />
                    ) : (
                      <Autocomplete
                        fullWidth
                        disablePortal
                        ListboxProps={{
                          sx: {
                            li: {
                              padding: '8px',
                              '&:hover': {
                                backgroundColor: '#fafafa',
                                cursor: 'pointer',
                              },
                            },
                          },
                        }}
                        value={schemaTableMapped ? map[lookup][fieldId] : ''}
                        options={
                          schemaTableMapped && sourceFields[schemaTableMapped]
                            ? Object.values(sourceFields[schemaTableMapped].selected).map((fields) => fields)
                            : []
                        }
                        size="small"
                        sx={{
                          '.hotglue-w-MuiFormLabel-asterisk': {
                            color: ({ palette }) => palette.error.main,
                          },
                          '& button': {
                            border: 'none',
                          },
                          '&:hover button': {
                            visibility: 'visible',
                          },
                          '.hotglue-w-MuiAutocomplete-endAdornment': {
                            top: 'calc(40%)',
                          },
                        }}
                        renderInput={(params) => (
                          <TextField
                            variant="standard"
                            label="Field"
                            {...params}
                            required={field.required ?? false}
                            error={!!error[`${table}/${fieldId}`]}
                          />
                        )}
                        onChange={(e, sourceField) =>
                          handleMapField({
                            schemaField: field.id,
                            sourceField,
                            sourceTable: schemaTableMapped,
                            schemaTable: table,
                            type,
                          })
                        }
                      />
                    )}
                  </Box>
                </Box>
              );
            })}
        </Box>
      </Collapse>
    </Box>
  );
};

export default RowMap;
